import animateScrollTo from "animated-scroll-to";

interface IOptions {
  // If the children to be scrolled are slotted component we can't rely on the value given by element.offsetLeft
  // because it returns different values in different browsers. The offsetParent is calcualted differently for
  // slotted components (Firefox searches only in shadow DOM while Chrome searches all the DOM).
  firstChild?: HTMLElement | null;
  scrollContainer?: any; // DOM element that should be scrolled
  targetPosition?: string; // start, center, end
  direction?: string; // horizontally, vertically
  offset?: number; // positive or negative number
  maxDuration?: number; // Maximum duration of the scroll animation
  minDuration?: number; // Minimum duration of the scroll animation
  speed?: number; // Duration of the scroll per 1000px
  cancelOnUserAction?: boolean; // Indicated if scroll animation should be canceled on user action (scroll/keypress/touch)
  easing?: (t: number) => number; // easing function
}

/**
 * Function to scroll an element within its parent by using either scrollTo if available or
 * do the scroll animation manually instead.
 * @param element: target element
 * @param userOptions: can be used for overwriting default options
 */
export async function scrollToElement(element: HTMLElement, userOptions: IOptions): Promise<boolean> {
  const defaultOptions: IOptions = {
    scrollContainer: window,
    targetPosition: "start",
    direction: "vertically",
    offset: 0,
    maxDuration: 1500,
    minDuration: 250,
    speed: 650,
    cancelOnUserAction: false,
    easing: EasingFunctions.easeInOutQuad
  };

  let options: IOptions = {
    ...defaultOptions,
    ...userOptions
  };

  return animateScrollToWrapper(element, options);
}

/**
 * Scroll function based on animateScrollTo plugin (https://github.com/Stanko/animated-scroll-to).
 */
async function animateScrollToWrapper(element: HTMLElement, options: IOptions): Promise<boolean> {
  let positioningOffset;

  let animateScrollToOptions = {
    cancelOnUserAction: options.cancelOnUserAction,
    easing: options.easing,
    elementToScroll: options.scrollContainer,
    maxDuration: options.maxDuration,
    minDuration: options.minDuration,
    speed: options.speed,
    horizontalOffset: 0,
    verticalOffset: 0
  };

  switch (options.direction) {
    case "horizontally":
      let scrollContainerWidth: number;
      let childOffsetLeft = options.firstChild
        ? element.getBoundingClientRect().left - options.firstChild.getBoundingClientRect().left
        : element.offsetLeft;

      scrollContainerWidth =
        options.scrollContainer === window ? window.innerWidth : options.scrollContainer.offsetWidth;

      switch (options.targetPosition) {
        case "center":
          positioningOffset = element.offsetWidth / 2 - scrollContainerWidth / 2;
          animateScrollToOptions.horizontalOffset = options.offset + positioningOffset;
          return animateScrollTo([childOffsetLeft, null], animateScrollToOptions);
        case "end":
          positioningOffset = -(scrollContainerWidth - element.offsetWidth);
          animateScrollToOptions.horizontalOffset = options.offset + positioningOffset;
          return animateScrollTo([childOffsetLeft, null], animateScrollToOptions);
        default:
          // start
          animateScrollToOptions.horizontalOffset = options.offset;
          return animateScrollTo([childOffsetLeft, null], animateScrollToOptions);
      }
    default:
      // vertically
      let scrollContainerHeight: number;
      let childOffsetTop = options.firstChild
        ? element.getBoundingClientRect().top - options.firstChild.getBoundingClientRect().top
        : element.offsetTop;

      scrollContainerHeight =
        options.scrollContainer === window ? window.innerHeight : options.scrollContainer.offsetHeight;

      switch (options.targetPosition) {
        case "center":
          positioningOffset = element.offsetHeight / 2 - scrollContainerHeight / 2;
          animateScrollToOptions.verticalOffset = options.offset + positioningOffset;
          return animateScrollTo([null, childOffsetTop], animateScrollToOptions);
        case "end":
          positioningOffset = -(scrollContainerHeight - element.offsetHeight);
          animateScrollToOptions.verticalOffset = options.offset + positioningOffset;
          return animateScrollTo([null, childOffsetTop], animateScrollToOptions);
        default:
          // start of the DOM/Element provided
          animateScrollToOptions.verticalOffset = options.offset;
          return animateScrollTo(element, animateScrollToOptions);
      }
  }
}

/*
 * Easing Functions
 * https://gist.github.com/gre/1650294
 */
const EasingFunctions = {
  // no easing, no acceleration
  linear: (t) => {
    return t;
  },
  // accelerating from zero velocity
  easeInQuad: (t) => {
    return t * t;
  },
  // decelerating to zero velocity
  easeOutQuad: (t) => {
    return t * (2 - t);
  },
  // acceleration until halfway, then deceleration
  easeInOutQuad: (t) => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  // accelerating from zero velocity
  easeInCubic: (t) => {
    return t * t * t;
  },
  // decelerating to zero velocity
  easeOutCubic: (t) => {
    return --t * t * t + 1;
  },
  // acceleration until halfway, then deceleration
  easeInOutCubic: (t) => {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  // accelerating from zero velocity
  easeInQuart: (t) => {
    return t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuart: (t) => {
    return 1 - --t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuart: (t) => {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  },
  // accelerating from zero velocity
  easeInQuint: (t) => {
    return t * t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuint: (t) => {
    return 1 + --t * t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuint: (t) => {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  }
};
